import { useEffect, useState } from "react";
import { ScrollText, Feather, X } from "lucide-react";

// Ambient Raven Fortunes — frontend-only, no backend, no storage beyond sessionStorage.
// 75% chance per browser session, random 25–75s delay, at most once per session.
const MESSAGES = [
  "The lanterns are lit for you tonight.",
  "You are exactly where you need to be.",
  "Rest, if you will. The road can wait.",
  "Your story matters more than you know.",
  "The night is full of quiet magic.",
  "You are not alone on this journey.",
  "The fire remembers your name.",
  "Take what you need. Leave what you can.",
  "Even the darkest nights hold a promise of dawn.",
  "Your heart has brought you to the right place.",
  "Words have power. Yours most of all.",
  "There is strength in being gentle.",
  "You are a light in someone's dark.",
  "The best tales begin with a single step.",
  "Listen to the whispers; they know the way.",
  "Kindred spirits are never far away.",
  "May your path be gentle and true.",
  "Inspiration often comes when we least expect it.",
  "The Tavern keeps your secrets safe.",
  "May peace find you before you leave us.",
];

// ----- Module-level singleton scheduler (StrictMode-safe) -----
// Living outside the component means React 18's double-mount in dev cannot
// re-roll or cancel the timer, while sessionStorage keeps it to once per session.
let scheduled = false;
let fired = false;
let chosenMsg = "";
const listeners = new Set();

function scheduleRaven() {
  if (scheduled) return; // only decide/schedule once per page load
  scheduled = true;

  let decided = null;
  try { decided = sessionStorage.getItem("raven_fortune"); } catch (e) { /* ignore */ }
  if (decided) return; // already handled this session (across reloads)

  const roll = Math.random() < 0.75; // 75% chance per session
  try { sessionStorage.setItem("raven_fortune", roll ? "yes" : "no"); } catch (e) { /* ignore */ }
  if (!roll) return;

  chosenMsg = MESSAGES[Math.floor(Math.random() * MESSAGES.length)];
  const delay = 25000 + Math.random() * 50000; // 25–75 seconds
  setTimeout(() => {
    fired = true;
    listeners.forEach((fn) => fn());
  }, delay);
}

export default function RavenFortune() {
  const [phase, setPhase] = useState(fired ? "scroll" : "hidden"); // hidden | scroll | message

  useEffect(() => {
    scheduleRaven();
    const onFire = () => setPhase((p) => (p === "hidden" ? "scroll" : p));
    listeners.add(onFire);
    if (fired) onFire();
    return () => { listeners.delete(onFire); };
  }, []);

  if (phase === "hidden") return null;

  return (
    <div
      data-testid="raven-fortune"
      style={{ position: "fixed", left: "1.25rem", bottom: "1.25rem", zIndex: 60, maxWidth: "min(300px, 86vw)" }}
    >
      <style>{`
        @keyframes ravenFloatIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ravenBob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
        @keyframes ravenPop { from { opacity: 0; transform: translateY(8px) scale(.96); } to { opacity: 1; transform: none; } }
        .raven-scroll-btn { animation: ravenFloatIn .6s ease-out, ravenBob 3.6s ease-in-out .6s infinite; }
        .raven-msg-card { animation: ravenPop .35s ease-out; }
        @media (prefers-reduced-motion: reduce) {
          .raven-scroll-btn { animation: none; }
          .raven-msg-card { animation: none; }
        }
      `}</style>

      {phase === "message" && (
        <div className="parchment-card raven-msg-card p-4 mb-3" data-testid="raven-message" style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setPhase("hidden")}
            aria-label="Close the raven's scroll"
            data-testid="raven-close"
            style={{ position: "absolute", top: 6, right: 6, color: "var(--muted)", background: "transparent", border: "none", cursor: "pointer" }}
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            <Feather className="h-4 w-4" style={{ color: "var(--gold)" }} />
            <span className="page-eyebrow" style={{ fontSize: "0.65rem" }}>a raven's fortune</span>
          </div>
          <p className="tavern-script mt-2" style={{ color: "var(--orchid-light)", fontSize: "1.35rem", lineHeight: 1.4 }}>
            {chosenMsg}
          </p>
        </div>
      )}

      {phase === "scroll" && (
        <button
          type="button"
          onClick={() => setPhase("message")}
          className="raven-scroll-btn"
          data-testid="raven-scroll"
          aria-label="A raven left you a scroll — open it"
          title="A raven left you a scroll…"
          style={{
            display: "grid",
            placeItems: "center",
            height: 52,
            width: 52,
            borderRadius: "9999px",
            background: "rgba(26,10,46,0.92)",
            border: "1px solid rgba(212,175,55,0.5)",
            boxShadow: "0 0 20px rgba(212,175,55,0.35)",
            cursor: "pointer",
          }}
        >
          <ScrollText className="h-6 w-6" style={{ color: "var(--gold)" }} />
        </button>
      )}
    </div>
  );
}
